import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { compressImage } from '../lib/imageCompression';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Send, MessageCircle, Loader2, ThumbsUp, Calendar, Shield, ChevronDown, ChevronUp, Paperclip, FileText, X, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import ScheduleInterviewModal from '../components/ScheduleInterviewModal';
import ApplicantDetailModal from '../components/ApplicantDetailModal';
import InterviewCard from '../components/InterviewCard';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../components/ui/collapsible';
import { linkifyText } from '../lib/linkify';
import { v5 as uuidv5 } from 'uuid';

const THREAD_NAMESPACE = '1b671a64-40d5-491e-99b0-da01ff1f3341';

const displayName = (user) => {
  if (!user) return 'Unknown User';
  if (user.role === 'employer' || user.role === 'owner') {
    if (user.is_private_household) return 'Private Household';
    return user.business_name || 'Employer';
  }
  return user.name || 'Unknown User';
};

const Messages = ({ profile, setProfile }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const draftAppliedRef = useRef(null);
  // Once the pre-written message was sent (or they moved to another chat), forget it so a refresh or
  // Back doesn't put the same text in the box again. Kept until then so a remount still restores it.
  const dropDraftFromHistory = () => {
    if (location.state?.draft) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  };
  const [sending, setSending] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [reactions, setReactions] = useState({});
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [tappedMessageId, setTappedMessageId] = useState(null);
  const [menuMessageId, setMenuMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [employerJobs, setEmployerJobs] = useState([]);
  const [selectedJobForInterview, setSelectedJobForInterview] = useState(null);
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [threadInterviews, setThreadInterviews] = useState([]);
  const [interviewBannerOpen, setInterviewBannerOpen] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const scrollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const realtimeChannelRef = useRef(null);
  const reactionsChannelRef = useRef(null);
  const interviewsChannelRef = useRef(null);
  const recipientOnlineChannelRef = useRef(null);
  const recipientRef = useRef(null);

  // UNIVERSAL FIX: Auto-scroll to bottom whenever messages change
  // Respects prefers-reduced-motion for accessibility
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      messagesEndRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleAttachmentSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isImage && !isPdf) {
      toast.error('Only images and PDFs are supported');
      e.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      e.target.value = '';
      return;
    }

    const type = isPdf ? 'pdf' : 'image';
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    setAttachmentFile(file);
    setAttachmentPreview({ url: previewUrl, name: file.name, type });
    e.target.value = '';
  };

  const clearAttachment = () => {
    if (attachmentPreview?.url) {
      URL.revokeObjectURL(attachmentPreview.url);
    }
    setAttachmentFile(null);
    setAttachmentPreview(null);
  };

  const uploadAttachment = async (file, token) => {
    // Compress image attachments before uploading (skip PDFs and other non-image files)
    let fileToUpload = file;
    if (file.type.startsWith('image/')) {
      fileToUpload = await compressImage(file, 'jobImage');
    }
    const formData = new FormData();
    formData.append('file', fileToUpload);
    const res = await api.post('/api/upload/message-attachment', formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': undefined },
      timeout: UPLOAD_TIMEOUT,
    });
    return res.data.attachment;
  };

  // Keep recipientRef in sync for stable realtime callback closure
  useEffect(() => { recipientRef.current = recipient; }, [recipient]);

  const isOnline = (lastSeenAt) => {
    if (!lastSeenAt) return false;
    return (Date.now() - new Date(lastSeenAt).getTime()) < 5 * 60 * 1000;
  };

  const formatLastSeen = (lastSeenAt) => {
    if (!lastSeenAt) return null;
    const diff = Date.now() - new Date(lastSeenAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `Last seen ${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Last seen ${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `Last seen ${days}d ago`;
  };

  const fetchThreadInterviews = useCallback(async (recipientId) => {
    if (!recipientId) {
      setThreadInterviews([]);
      return;
    }
    try {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess?.session?.access_token) return;
      // Fetch without upcoming=true so cancelled/declined interviews are included
      const res = await api.get('/api/interviews?limit=10', {
        headers: { Authorization: `Bearer ${sess.session.access_token}` }
      });
      const allInterviews = (res.data.interviews || res.data || []).filter(
        iv => iv.employer_id === recipientId || iv.applicant_id === recipientId
      );
      // Show active upcoming interviews + recently cancelled/declined (within 24h)
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const interviews = allInterviews.filter(iv => {
        if (iv.status === 'completed') return false;
        if (iv.status === 'cancelled' || iv.status === 'declined') {
          return iv.updated_at >= oneDayAgo || iv.cancelled_at >= oneDayAgo;
        }
        return true;
      });
      setThreadInterviews(interviews);
      // Auto-expand if any interview needs action or was recently cancelled
      if (interviews.some(iv => ['pending', 'rescheduled', 'cancelled', 'declined'].includes(iv.status))) {
        setInterviewBannerOpen(true);
      }
    } catch (err) {
      console.error('Failed to fetch thread interviews:', err);
    }
  }, []);

  // Fetch interviews when recipient changes
  useEffect(() => {
    if (recipient?.id) fetchThreadInterviews(recipient.id);
    else setThreadInterviews([]);
  }, [recipient?.id, fetchThreadInterviews]);

  // UNIVERSAL FIX: Refetch on tab visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && selectedThread) {
        console.log('Tab visible, refetching messages');
        fetchMessages(selectedThread);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedThread]);

  // Close the message action menu on outside-click or Escape.
  useEffect(() => {
    if (!menuMessageId) return;
    const closeOnClick = () => setMenuMessageId(null);
    const closeOnKey = (e) => { if (e.key === 'Escape') setMenuMessageId(null); };
    document.addEventListener('mousedown', closeOnClick);
    document.addEventListener('keydown', closeOnKey);
    return () => {
      document.removeEventListener('mousedown', closeOnClick);
      document.removeEventListener('keydown', closeOnKey);
    };
  }, [menuMessageId]);

  const handleSaveEdit = async (id) => {
    const text = editingText.trim();
    if (!text) { toast.error('Message cannot be empty — use Unsend instead.'); return; }
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) { toast.error('Please log in again.'); return; }
    const orig = messages.find(m => m.id === id);          // full snapshot for rollback
    setMessages(prev => prev.map(m => m.id === id ? { ...m, body: text, edited_at: new Date().toISOString() } : m));
    setEditingMessageId(null);
    try {
      await api.patch(`/api/messages/${id}`, { body: text }, { headers: { Authorization: `Bearer ${session.session.access_token}` } });
      // No server reconcile: the PATCH response omits the client-only attachment_signed_url,
      // so we keep the optimistic edited body.
    } catch (e) {
      // Roll back BOTH body and edited_at; guard undefined (realtime may have replaced the row).
      setMessages(prev => prev.map(m => (m.id === id && orig) ? { ...m, body: orig.body, edited_at: orig.edited_at } : m));
      toast.error('Could not edit the message.');
    }
  };

  const handleUnsend = async (id) => {
    if (!window.confirm('Unsend this message? It will be removed for everyone.')) return;
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) { toast.error('Please log in again.'); return; }
    const snapshot = messages.find(m => m.id === id);
    setMessages(prev => prev.map(m => m.id === id ? { ...m, deleted_at: new Date().toISOString(), body: null, attachment_signed_url: null, attachment_type: null } : m));
    try {
      await api.delete(`/api/messages/${id}`, { headers: { Authorization: `Bearer ${session.session.access_token}` } });
    } catch (e) {
      // Guard undefined snapshot (concurrent realtime replace) — mapping to undefined would crash render.
      setMessages(prev => prev.map(m => (m.id === id && snapshot) ? snapshot : m));
      toast.error('Could not unsend the message.');
    }
  };

  const fetchThreads = async () => {
    if (!profile) return;

    try {
      console.log('Fetching threads for profile:', profile.id, 'Role:', profile.role);
      
      const { data, error } = await supabase
        .from('messages')
        .select('thread_id, sender_id, receiver_id, created_at, body, is_read, attachment_type, deleted_at')
        .or(`sender_id.eq.${profile.id},receiver_id.eq.${profile.id}`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching threads:', error);
        throw error;
      }

      console.log('Raw messages data:', data?.length, 'messages');

      // Build thread map from messages
      const threadMap = new Map();

      if (data && data.length > 0) {
        for (const msg of data) {
          if (!threadMap.has(msg.thread_id)) {
            const otherUserId = msg.sender_id === profile.id ? msg.receiver_id : msg.sender_id;
            let preview;
            if (msg.deleted_at) {
              preview = msg.sender_id === profile.id ? 'You unsent a message' : 'This message was deleted';
            } else {
              preview = msg.body;
              if (!preview && msg.attachment_type === 'image') preview = 'Sent a photo';
              else if (!preview && msg.attachment_type === 'pdf') preview = 'Sent a document';
            }

            threadMap.set(msg.thread_id, {
              thread_id: msg.thread_id,
              other_user_id: otherUserId,
              last_message: preview,
              last_message_time: msg.created_at,
              unread_count: 0
            });
          }

          // Count unread messages for this user (messages sent TO them that are unread)
          const threadData = threadMap.get(msg.thread_id);
          if (msg.receiver_id === profile.id && msg.is_read === false && !msg.deleted_at) {
            threadData.unread_count++;
          }
        }
      }

      // Merge interview-only threads (interviews without prior messages)
      try {
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.access_token) {
          const ivRes = await api.get('/api/interviews?limit=50', {
            headers: { Authorization: `Bearer ${session.session.access_token}` }
          });
          const interviews = ivRes.data.interviews || ivRes.data || [];
          const existingOtherIds = new Set(Array.from(threadMap.values()).map(t => t.other_user_id));

          for (const iv of interviews) {
            if (iv.status === 'completed') continue;
            const otherUserId = iv.employer_id === profile.id ? iv.applicant_id : iv.employer_id;
            if (existingOtherIds.has(otherUserId)) continue;

            // Generate deterministic thread_id matching startConversation
            const ids = [profile.id, otherUserId].sort();
            const threadId = uuidv5(`${ids[0]}-${ids[1]}`, THREAD_NAMESPACE);

            threadMap.set(threadId, {
              thread_id: threadId,
              other_user_id: otherUserId,
              last_message: 'Interview scheduled',
              last_message_time: iv.created_at,
              unread_count: 0
            });
            existingOtherIds.add(otherUserId);
          }
        }
      } catch (ivErr) {
        console.log('Could not fetch interviews for threads:', ivErr);
      }

      const threadsArray = Array.from(threadMap.values());
      console.log('Unique threads (incl interview-only):', threadsArray.length);

      if (threadsArray.length > 0) {
        const userIds = threadsArray.map(t => t.other_user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name, email, avatar_url, last_seen_at, business_name, role, is_private_household')
          .in('id', userIds);

        const threadsWithProfiles = threadsArray.map(thread => {
          const otherProfile = profiles?.find(p => p.id === thread.other_user_id);
          return {
            ...thread,
            other_user: otherProfile
          };
        });

        // Sort by most recent
        threadsWithProfiles.sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time));

        setThreads(threadsWithProfiles);
      } else {
        console.log('No messages or interviews found');
        setThreads([]);
      }
    } catch (error) {
      console.error('❌ Failed to fetch threads:', error);
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async (userId) => {
    if (!profile) return;
    
    try {
      const ids = [profile.id, userId].sort();
      const threadId = uuidv5(`${ids[0]}-${ids[1]}`, THREAD_NAMESPACE);
      
      console.log('Starting conversation with user:', userId, 'thread:', threadId);
      setSelectedThread(threadId);
      setThreadInterviews([]);
      setInterviewBannerOpen(true);

      // CRITICAL FIX: Hide sidebar on mobile to show chat area
      setShowSidebar(false);

      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (data) {
        setRecipient(data);
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get(`/api/messages/thread/${threadId}`, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      console.log('Fetched messages:', response.data.messages?.length);
      setMessages(response.data.messages || []);
      
      if (response.data.messages && response.data.messages.length > 0) {
        const unreadIds = response.data.messages
          .filter(m => m.receiver_id === profile.id && m.is_read === false)
          .map(m => m.id);

        if (unreadIds.length > 0) {
          const { error: readErr } = await supabase
            .from('messages')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .in('id', unreadIds);
          if (readErr) console.error('Mark-as-read error:', readErr);
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setMessages([]);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const hasText = newMessage.trim();
    const hasAttachment = !!attachmentFile;
    if ((!hasText && !hasAttachment) || !selectedThread || !recipient) return;

    setSending(true);
    const tempMessage = newMessage;
    const tempAttachment = attachmentPreview;
    const tempFile = attachmentFile;
    const tempId = 'temp-' + Date.now();

    // Optimistically add message to UI
    const optimisticMessage = {
      id: tempId,
      sender_id: profile.id,
      receiver_id: recipient.id,
      body: tempMessage,
      created_at: new Date().toISOString(),
      thread_id: selectedThread,
      is_optimistic: true,
      ...(tempAttachment && {
        attachment_type: tempAttachment.type,
        attachment_name: tempAttachment.name,
        attachment_signed_url: tempAttachment.url
      })
    };

    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');
    clearAttachment();
    dropDraftFromHistory();

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setNewMessage(tempMessage);
        return;
      }

      // Upload attachment first if present
      let attachmentData = null;
      if (tempFile) {
        setUploadingAttachment(true);
        try {
          attachmentData = await uploadAttachment(tempFile, session.session.access_token);
        } catch (uploadErr) {
          console.error('Failed to upload attachment:', uploadErr);
          toast.error('Failed to upload attachment');
          setMessages(prev => prev.filter(m => m.id !== tempId));
          setNewMessage(tempMessage);
          setSending(false);
          setUploadingAttachment(false);
          return;
        }
        setUploadingAttachment(false);
      }

      const payload = {
        thread_id: selectedThread,
        receiver_id: recipient.id,
        body: tempMessage
      };
      if (attachmentData) {
        payload.attachment_url = attachmentData.url;
        payload.attachment_name = attachmentData.name;
        payload.attachment_type = attachmentData.type;
      }

      const result = await api.post('/api/messages', payload, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      console.log('Message sent successfully:', result.data);

      // Refetch thread to get signed URLs for the new message
      if (attachmentData) {
        fetchMessages(selectedThread);
      } else {
        setMessages(prev => prev.map(m =>
          m.id === tempId ? result.data.message : m
        ));
      }

      fetchThreads();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(tempMessage);
    } finally {
      setSending(false);
      setUploadingAttachment(false);
    }
  };

  const handleThreadClick = async (thread) => {
    // Don't carry a pre-written message meant for one applicant into someone else's chat.
    if (draftAppliedRef.current && newMessage === draftAppliedRef.current) setNewMessage('');
    dropDraftFromHistory();
    setSelectedThread(thread.thread_id);
    setThreadInterviews([]);
    setInterviewBannerOpen(true);
    setRecipient(thread.other_user); // Set immediately for fast UI
    // Fetch full profile for the modal
    if (thread.other_user?.id) {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', thread.other_user.id)
        .single();
      if (data) setRecipient(data);
    }
  };

  const fetchReactions = useCallback(async (threadId) => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess?.session?.access_token) return;
      const res = await api.get(`/api/messages/thread/${threadId}/reactions`, {
        headers: { Authorization: `Bearer ${sess.session.access_token}` }
      });
      setReactions(res.data.reactions || {});
    } catch (err) {
      console.error('Failed to fetch reactions:', err);
    }
  }, []);

  const toggleReaction = async (messageId) => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess?.session?.access_token) return;
      const res = await api.post(`/api/messages/${messageId}/reactions`, {}, {
        headers: { Authorization: `Bearer ${sess.session.access_token}` }
      });
      // Optimistic update
      setReactions(prev => {
        const copy = { ...prev };
        if (res.data.action === 'added') {
          if (!copy[messageId]) copy[messageId] = [];
          copy[messageId] = [...copy[messageId], { user_id: profile.id, reaction: 'thumbs_up' }];
        } else {
          copy[messageId] = (copy[messageId] || []).filter(r => r.user_id !== profile.id);
          if (copy[messageId].length === 0) delete copy[messageId];
        }
        return copy;
      });
    } catch (err) {
      console.error('Failed to toggle reaction:', err);
      toast.error('Failed to react');
    }
  };

  const handleScheduleInterview = async () => {
    if (profile.role !== 'employer' || !recipient) return;
    try {
      // Look up which of this employer's jobs the recipient applied for
      const { data: myJobs } = await supabase
        .from('jobs')
        .select('id, title')
        .eq('employer_id', profile.id);

      if (!myJobs || myJobs.length === 0) {
        toast.error('You have no jobs to schedule interviews for');
        return;
      }

      const jobIds = myJobs.map(j => j.id);
      const { data: applications } = await supabase
        .from('applications')
        .select('id, job_id')
        .eq('seeker_id', recipient.id)
        .in('job_id', jobIds);

      const appliedJobs = (applications || []).map(app => {
        const job = myJobs.find(j => j.id === app.job_id);
        return job ? { ...job, applicationId: app.id } : null;
      }).filter(Boolean);

      if (appliedJobs.length === 1) {
        setSelectedJobForInterview(appliedJobs[0]);
        setEmployerJobs(appliedJobs);
        setShowScheduleInterview(true);
      } else if (appliedJobs.length > 1) {
        setEmployerJobs(appliedJobs);
        setShowJobSelector(true);
      } else {
        // No applications found - fall back to all open jobs
        const res = await api.get(`/api/employers/${profile.id}/jobs?status=open`);
        const openJobs = (res.data.jobs || []).map(j => ({ ...j, applicationId: null }));
        if (openJobs.length === 0) {
          toast.error('You have no open jobs to schedule interviews for');
          return;
        }
        if (openJobs.length === 1) {
          setSelectedJobForInterview(openJobs[0]);
          setEmployerJobs(openJobs);
          setShowScheduleInterview(true);
        } else {
          setEmployerJobs(openJobs);
          setShowJobSelector(true);
        }
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      toast.error('Failed to load your jobs');
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Heartbeat: update last_seen_at every 60s while Messages page is open
  useEffect(() => {
    if (!session?.access_token) return;
    const sendHeartbeat = () => {
      api.post('/api/profile/heartbeat', {}, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      }).catch(() => {});
    };
    sendHeartbeat(); // Immediately on mount
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [session?.access_token]);

  // Realtime subscription for recipient's online status
  useEffect(() => {
    if (recipientOnlineChannelRef.current) {
      supabase.removeChannel(recipientOnlineChannelRef.current);
      recipientOnlineChannelRef.current = null;
    }
    if (!recipient?.id) return;

    const channel = supabase
      .channel(`online-status-${recipient.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${recipient.id}`
        },
        (payload) => {
          if (payload.new.last_seen_at) {
            setRecipient(prev => prev ? { ...prev, last_seen_at: payload.new.last_seen_at } : prev);
          }
        }
      )
      .subscribe();

    recipientOnlineChannelRef.current = channel;
    return () => {
      if (recipientOnlineChannelRef.current) {
        supabase.removeChannel(recipientOnlineChannelRef.current);
        recipientOnlineChannelRef.current = null;
      }
    };
  }, [recipient?.id]);

  // UNIVERSAL FIX: Single role-agnostic realtime subscription
  useEffect(() => {
    if (!profile?.id || !session) return;

    // Clean up previous channel
    if (realtimeChannelRef.current) {
      console.log('Cleaning up previous realtime channel');
      supabase.removeChannel(realtimeChannelRef.current);
    }

    console.log('Setting up UNIVERSAL realtime subscription for user:', profile.id, 'Role:', profile.role);
    
    // UNIVERSAL SUBSCRIPTION - works for ALL roles
    // Note: Supabase doesn't support OR in filters, so we listen to ALL messages
    // and filter in the callback
    const channel = supabase
      .channel(`messages-universal-${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        (payload) => {
          console.log('REALTIME INSERT detected:', payload.new);
          
          // CRITICAL: Only process if this user is involved
          if (payload.new.sender_id === profile.id || payload.new.receiver_id === profile.id) {
            console.log('Message is for current user');
            
            // Refetch threads to update list
            fetchThreads();
            
            // If currently viewing this thread, add message to list
            if (selectedThread === payload.new.thread_id) {
              console.log('Adding message to current thread');
              // If message has attachment, refetch to get signed URL
              if (payload.new.attachment_url) {
                fetchMessages(selectedThread);
              } else {
                setMessages(prev => {
                  const filtered = prev.filter(m => !m.id.toString().startsWith('temp-'));
                  if (filtered.some(m => m.id === payload.new.id)) {
                    return prev;
                  }
                  return [...filtered, payload.new];
                });
              }
            }
          } else {
            console.log('⏭️ Message not for current user, ignoring');
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
        },
        (payload) => {
          console.log('REALTIME UPDATE detected:', payload.new);
          
          // CRITICAL: Only process if this user is involved
          if (payload.new.sender_id === profile.id || payload.new.receiver_id === profile.id) {
            // Update message in current thread if applicable
            if (selectedThread === payload.new.thread_id) {
              setMessages(prev => prev.map(m => {
                if (m.id !== payload.new.id) return m;
                if (payload.new.deleted_at) {
                  // Unsent: never keep/display old content locally (belt-and-suspenders;
                  // the DB row is already nulled server-side).
                  return { ...m, ...payload.new, body: null, attachment_signed_url: null, attachment_type: null, attachment_name: null };
                }
                // Merge (not replace) so client-only fields like attachment_signed_url
                // survive an edit/read UPDATE.
                return { ...m, ...payload.new };
              }));
            }
            // An edit/unsend changes the sidebar preview + unread count — re-derive the
            // thread list so the sidebar reflects it live (setMessages alone won't).
            if (payload.new.deleted_at || payload.new.edited_at) {
              fetchThreads();
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('UNIVERSAL realtime fully active for', profile.role);
        }
      });

    realtimeChannelRef.current = channel;

    // Reactions realtime channel
    if (reactionsChannelRef.current) {
      supabase.removeChannel(reactionsChannelRef.current);
    }
    const reactionsChannel = supabase
      .channel(`reactions-${profile.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'message_reactions' },
        (payload) => {
          if (selectedThread) {
            fetchReactions(selectedThread);
          }
        }
      )
      .subscribe();
    reactionsChannelRef.current = reactionsChannel;

    // Interviews realtime channel
    if (interviewsChannelRef.current) {
      supabase.removeChannel(interviewsChannelRef.current);
    }
    const interviewsChannel = supabase
      .channel(`interviews-${profile.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'interviews' },
        (payload) => {
          const row = payload.new || payload.old;
          if (row && (row.employer_id === profile.id || row.applicant_id === profile.id)) {
            // Toast notification for status changes
            if (payload.eventType === 'UPDATE' && payload.new) {
              if (payload.new.status === 'cancelled') {
                toast.error('An interview has been cancelled', { duration: 5000 });
              } else if (payload.new.status === 'declined') {
                toast.error('An interview has been declined', { duration: 5000 });
              } else if (payload.new.status === 'confirmed') {
                toast.success('An interview has been confirmed', { duration: 5000 });
              } else if (payload.new.status === 'rescheduled') {
                toast('An interview reschedule has been requested', { duration: 5000 });
              }
            }
            fetchThreadInterviews(recipientRef.current?.id);
          }
        }
      )
      .subscribe();
    interviewsChannelRef.current = interviewsChannel;

    // Fetch initial data
    fetchThreads();

    return () => {
      console.log('Unsubscribing from realtime');
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
      }
      if (reactionsChannelRef.current) {
        supabase.removeChannel(reactionsChannelRef.current);
      }
      if (interviewsChannelRef.current) {
        supabase.removeChannel(interviewsChannelRef.current);
      }
    };
  }, [profile?.id, session, fetchReactions, fetchThreadInterviews]);

  useEffect(() => {
    const userId = searchParams.get('user');
    if (userId && profile) {
      startConversation(userId);
    }
  }, [searchParams, profile]);

  // A pre-written message from inside the app (e.g. an employer asking an applicant for missing
  // documents), passed in router state — never read from the URL, so an outside link can't pre-fill
  // it. Only placed in the box for review; nothing is sent until they press Send. Placed once, so a
  // later profile refresh can't overwrite what they've typed. Kept separate from the ?user= effect
  // so clearing it from history never re-opens that conversation.
  useEffect(() => {
    const draft = typeof location.state?.draft === 'string' ? location.state.draft : '';
    if (draft && profile && searchParams.get('user') && draftAppliedRef.current !== draft) {
      draftAppliedRef.current = draft;
      setNewMessage(draft.slice(0, 500));
    }
  }, [location.state, profile, searchParams]);

  useEffect(() => {
    if (selectedThread && profile) {
      console.log('Selected thread changed, fetching messages');
      fetchMessages(selectedThread);
      fetchReactions(selectedThread);
    }
  }, [selectedThread, fetchReactions]);

  // Guard against blank white screen
  if (!profile?.id || !session) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        <p className="mt-6 text-lg text-gray-600">Loading your messages...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="flex justify-center items-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="h-[calc(100dvh-56px-56px)] md:h-[calc(100dvh-56px)] flex relative" style={{ height: 'calc(100dvh - 56px - 56px)' }}>
        {/* Left Sidebar - Thread List */}
        <div className={`${
          showSidebar ? 'w-full md:w-80' : 'hidden md:flex md:w-80'
        } border-r bg-gray-50 flex flex-col absolute md:relative inset-0 md:inset-auto z-20 md:z-auto`}>
          <div className="p-4 border-b bg-white">
            <h2 className="font-heading text-xl font-semibold">Messages</h2>
          </div>
          
          <ScrollArea className="flex-1">
            {threads.length === 0 ? (
              <div className="p-8 text-center">
                <MessageCircle className="mx-auto mb-4 text-muted-foreground" size={48} />
                <p className="text-sm text-muted-foreground">{t('messaging.noConversations')}</p>
                <p className="text-xs text-muted-foreground mt-2">{t('messaging.startConversation')}</p>
              </div>
            ) : (
              <div className="divide-y">
                {threads.map((thread) => (
                  <div
                    key={thread.thread_id}
                    className={`p-4 hover:bg-white cursor-pointer transition-colors ${
                      selectedThread === thread.thread_id ? 'bg-white border-l-4 border-l-primary' : ''
                    }`}
                    onClick={() => {
                      handleThreadClick(thread);
                      setShowSidebar(false);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Avatar>
                          <AvatarFallback>{displayName(thread.other_user)?.[0] || 'U'}</AvatarFallback>
                        </Avatar>
                        {thread.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                            {thread.unread_count > 99 ? '99+' : thread.unread_count}
                          </div>
                        )}
                        {isOnline(thread.other_user?.last_seen_at) && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`truncate ${thread.unread_count > 0 ? 'font-bold' : 'font-medium'}`}>
                          {displayName(thread.other_user)}
                        </p>
                        <p className={`text-sm truncate ${thread.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                          {thread.last_message}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Right Side - Chat Area */}
        {!selectedThread ? (
          <div className="flex-1 flex items-center justify-center p-8" data-testid="empty-chat">
            <div className="text-center">
              <MessageCircle className="mx-auto mb-4 text-muted-foreground" size={64} />
              <h2 className="font-heading text-2xl font-semibold mb-2">Select a conversation</h2>
              <p className="text-muted-foreground">Choose a conversation from the left to start chatting</p>
            </div>
          </div>
        ) : (
          <div className={`${
            showSidebar ? 'hidden md:flex' : 'flex'
          } flex-1 flex-col w-full h-full`}>
            <div className="border-b px-4 py-3 bg-background flex items-center gap-3 flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setShowSidebar(true)}
              >
                <MessageCircle size={20} />
              </Button>
              <button
                className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity text-left"
                onClick={() => setShowProfileDialog(true)}
              >
                <div className="relative">
                  <Avatar>
                    {recipient?.avatar_url ? (
                      <img src={recipient.avatar_url} alt="" className="h-full w-full object-cover rounded-full" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <AvatarFallback>{displayName(recipient)?.[0] || 'U'}</AvatarFallback>
                    )}
                  </Avatar>
                  {isOnline(recipient?.last_seen_at) && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium flex items-center gap-1">
                    {displayName(recipient)}
                    {recipient?.verified && <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />}
                  </p>
                  <p className="text-xs truncate">
                    {isOnline(recipient?.last_seen_at) ? (
                      <span className="text-green-600 font-medium">Online</span>
                    ) : (
                      <span className="text-muted-foreground">{formatLastSeen(recipient?.last_seen_at) || recipient?.town || recipient?.email}</span>
                    )}
                  </p>
                </div>
              </button>
              {profile.role === 'employer' && recipient && (
                <Button
                  size="sm"
                  onClick={handleScheduleInterview}
                  className="flex-shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <Calendar size={16} className="md:mr-2" />
                  <span className="hidden md:inline">Schedule Interview</span>
                </Button>
              )}
            </div>

            {threadInterviews.length > 0 && (
              <Collapsible open={interviewBannerOpen} onOpenChange={setInterviewBannerOpen} className="border-b flex-shrink-0">
                <CollapsibleTrigger asChild>
                  <button className="w-full px-4 py-2 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors text-sm">
                    <div className="flex flex-wrap items-center gap-2 gap-y-1">
                      <Calendar size={16} className="text-primary" />
                      <span className="font-medium">
                        {threadInterviews.length === 1 ? '1 Active Interview' : `${threadInterviews.length} Active Interviews`}
                      </span>
                      {threadInterviews.some(iv => iv.status === 'cancelled' || iv.status === 'declined') && (
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full font-medium">
                          Cancelled
                        </span>
                      )}
                      {threadInterviews.some(iv => iv.status === 'pending' || iv.status === 'rescheduled') && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full font-medium">
                          Action needed
                        </span>
                      )}
                    </div>
                    {interviewBannerOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-4 py-3 space-y-3 bg-blue-50/50 max-h-[25vh] md:max-h-[30vh] overflow-y-auto">
                    {threadInterviews.map((interview) => (
                      <InterviewCard
                        key={interview.id}
                        interview={interview}
                        isEmployer={profile.role === 'employer'}
                        onUpdate={() => fetchThreadInterviews(recipient?.id)}
                      />
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            <ScrollArea className="flex-1 p-4 overflow-y-auto" ref={scrollRef}>
              <div className="space-y-2 pb-4">
                {messages.map((message, idx) => {
                  const isOwn = message.sender_id === profile.id;
                  const isOptimistic = message.is_optimistic || message.id.toString().startsWith('temp-');
                  const msgReactions = reactions[message.id] || [];
                  const reactionCount = msgReactions.length;
                  const hasMyReaction = msgReactions.some(r => r.user_id === profile.id);

                  // "Seen" indicator: only on the last message I SENT, only if the recipient read it
                  const isLastOwnMessage = isOwn && !isOptimistic && !message.deleted_at &&
                    messages.slice(idx + 1).every(m => m.sender_id !== profile.id || m.is_optimistic || m.id.toString().startsWith('temp-') || m.deleted_at);

                  // Date separator: show when the day changes between messages
                  const msgDate = new Date(message.created_at);
                  const prevMsg = idx > 0 ? messages[idx - 1] : null;
                  const prevDate = prevMsg ? new Date(prevMsg.created_at) : null;
                  const showDateSeparator = !prevDate ||
                    msgDate.toDateString() !== prevDate.toDateString();

                  // Full date on tap/click
                  const showFullDate = tappedMessageId === message.id;

                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <div className="flex items-center justify-center my-3">
                          <span className="text-[11px] text-muted-foreground bg-gray-100 px-3 py-1 rounded-full">
                            {msgDate.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: msgDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined })}
                          </span>
                        </div>
                      )}
                      {message.deleted_at ? (
                        <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-xs`}>
                            <p className="text-xs italic text-muted-foreground px-3 py-2">
                              {isOwn ? 'You unsent a message' : 'This message was deleted'}
                            </p>
                          </div>
                        </div>
                      ) : (
                      <div
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
                        onMouseEnter={() => !isOptimistic && setHoveredMessageId(message.id)}
                        onMouseLeave={() => setHoveredMessageId(null)}
                      >
                        <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-xs`}>
                          <div className="relative">
                            <Card
                              className={`${isOwn ? 'bg-primary text-primary-foreground' : 'bg-muted'} cursor-pointer`}
                              onClick={() => setTappedMessageId(prev => prev === message.id ? null : message.id)}
                            >
                              <CardContent className="p-3">
                                {message.attachment_type === 'image' && message.attachment_signed_url && (
                                  <a href={message.attachment_signed_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                                    <img
                                      src={message.attachment_signed_url}
                                      alt={message.attachment_name || 'Image'}
                                      className="max-h-64 rounded-lg mb-1 object-cover"
                                      loading="lazy"
                                      onError={(e) => { e.target.alt = 'Image failed to load'; e.target.className = 'max-h-64 rounded-lg mb-1 bg-muted p-4 text-sm text-muted-foreground'; }}
                                    />
                                  </a>
                                )}
                                {message.attachment_type === 'pdf' && (message.attachment_signed_url || message.attachment_name) && (
                                  message.attachment_signed_url ? (
                                    <a
                                      href={message.attachment_signed_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={e => e.stopPropagation()}
                                      className={`flex items-center gap-2 mb-1 px-2 py-1.5 rounded ${isOwn ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                                    >
                                      <FileText size={18} className="flex-shrink-0" />
                                      <span className="text-sm truncate">{message.attachment_name || 'Document.pdf'}</span>
                                    </a>
                                  ) : (
                                    <div className={`flex items-center gap-2 mb-1 px-2 py-1.5 rounded ${isOwn ? 'bg-white/10' : 'bg-gray-100'}`}>
                                      <FileText size={18} className="flex-shrink-0" />
                                      <span className="text-sm truncate">{message.attachment_name || 'Document.pdf'}</span>
                                    </div>
                                  )
                                )}
                                {editingMessageId === message.id ? (
                                  <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      className="text-sm border rounded px-2 py-1 text-foreground w-full"
                                      value={editingText}
                                      autoFocus
                                      onChange={(e) => setEditingText(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); handleSaveEdit(message.id); }
                                        if (e.key === 'Escape') { setEditingMessageId(null); }
                                      }}
                                    />
                                    <div className="flex gap-2 text-xs">
                                      <button type="button" onClick={() => handleSaveEdit(message.id)} className="underline">Save</button>
                                      <button type="button" onClick={() => setEditingMessageId(null)} className="opacity-70">Cancel</button>
                                    </div>
                                  </div>
                                ) : (
                                  message.body && (
                                    <p className="text-sm whitespace-pre-wrap">{linkifyText(message.body, isOwn ? 'text-white/90 underline break-all' : 'text-blue-600 underline break-all')}</p>
                                  )
                                )}
                                {isOwn && isOptimistic && (
                                  <span className="text-xs text-primary-foreground/70 mt-1 block">
                                    {uploadingAttachment ? 'Uploading...' : t('messaging.sending')}
                                  </span>
                                )}
                              </CardContent>
                            </Card>

                            {/* Reaction button - visible on hover */}
                            {!isOptimistic && (hoveredMessageId === message.id) && (
                              <button
                                onClick={() => toggleReaction(message.id)}
                                className={`absolute ${isOwn ? '-left-10' : '-right-10'} top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-gray-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${
                                  hasMyReaction ? 'text-blue-600' : 'text-gray-400'
                                }`}
                                title="React"
                              >
                                <ThumbsUp size={16} />
                              </button>
                            )}
                          </div>

                          {message.edited_at && (
                            <span className="text-[10px] text-muted-foreground mt-0.5">Edited</span>
                          )}
                          {isOwn && !isOptimistic && (
                            <div className="relative mt-0.5">
                              <button
                                type="button"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={() => setMenuMessageId(menuMessageId === message.id ? null : message.id)}
                                className="text-muted-foreground/60 hover:text-muted-foreground p-1"
                                aria-label="Message actions"
                              >
                                <MoreHorizontal size={14} />
                              </button>
                              {menuMessageId === message.id && (
                                <div className="absolute z-20 bottom-full right-0 mb-1 rounded-md border bg-white shadow-md text-sm overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
                                  {message.body && (
                                    <button
                                      type="button"
                                      className="block w-full px-4 py-1.5 text-left text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                                      onClick={() => { setEditingMessageId(message.id); setEditingText(message.body); setMenuMessageId(null); }}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="block w-full px-4 py-1.5 text-left text-red-600 hover:bg-gray-100 whitespace-nowrap"
                                    onClick={() => { setMenuMessageId(null); handleUnsend(message.id); }}
                                  >
                                    Unsend
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Reaction badge */}
                          {reactionCount > 0 && (
                            <button
                              onClick={() => toggleReaction(message.id)}
                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs -mt-1 border ${
                                hasMyReaction ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-white border-gray-200 text-gray-500'
                              } hover:bg-blue-100 transition-colors`}
                            >
                              <ThumbsUp size={10} />
                              {reactionCount}
                            </button>
                          )}

                          {/* Full date on tap (like Messenger) */}
                          {showFullDate && !isOptimistic && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {msgDate.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}

                          {/* Seen indicator: only on my last sent message, only if recipient read it */}
                          {isLastOwnMessage && message.read_at && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              Read at {new Date(message.read_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Invisible element to scroll to */}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            <div className="border-t bg-background flex-shrink-0">
              {attachmentPreview && (
                <div className="px-4 pt-3 pb-1 flex items-center gap-3 border-b bg-gray-50/80">
                  {attachmentPreview.type === 'image' && attachmentPreview.url ? (
                    <img src={attachmentPreview.url} alt="" className="h-14 w-14 rounded-lg object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="h-14 w-14 rounded-lg bg-gray-200 flex items-center justify-center">
                      <FileText size={24} className="text-gray-500" />
                    </div>
                  )}
                  <span className="text-sm text-muted-foreground truncate flex-1">{attachmentPreview.name}</span>
                  <button onClick={clearAttachment} className="p-2 rounded-full hover:bg-gray-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" type="button">
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>
              )}
              {messages.some((m) => m.message_type === 'announcement') ? (
                <div className="p-4 text-center text-xs text-gray-500 border-t bg-gray-50">
                  {t('messaging.announcementOneWay', 'This is a one-way announcement — you can’t reply. Tap the job link above to view and apply.')}
                </div>
              ) : (
              <form onSubmit={handleSendMessage} className="flex items-end gap-2 p-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleAttachmentSelect}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0 h-10 w-10"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                >
                  <Paperclip size={18} />
                </Button>
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if ((newMessage.trim() || attachmentFile) && !sending) {
                        handleSendMessage(e);
                      }
                    }
                  }}
                  placeholder={t('messaging.typeMessage')}
                  className="flex-1 rounded-xl resize-none min-h-[40px] max-h-[120px]"
                  rows={1}
                  disabled={sending}
                  data-testid="message-input"
                  autoFocus
                />
                <Button type="submit" disabled={sending || (!newMessage.trim() && !attachmentFile)} className="rounded-xl flex-shrink-0 h-10" data-testid="send-button">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </Button>
              </form>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Job selector dialog for scheduling interview */}
      {showJobSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowJobSelector(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-lg mb-3">Select a job for the interview</h3>
            <Select
              value={selectedJobForInterview?.id || ''}
              onValueChange={(val) => {
                const job = employerJobs.find(j => j.id === val);
                setSelectedJobForInterview(job);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a job..." />
              </SelectTrigger>
              <SelectContent>
                {employerJobs.map(job => (
                  <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowJobSelector(false)}>Cancel</Button>
              <Button
                size="sm"
                disabled={!selectedJobForInterview}
                onClick={() => {
                  setShowJobSelector(false);
                  setShowScheduleInterview(true);
                }}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Interview Modal */}
      {showScheduleInterview && selectedJobForInterview && recipient && (
        <ScheduleInterviewModal
          isOpen={showScheduleInterview}
          onClose={() => {
            setShowScheduleInterview(false);
            setSelectedJobForInterview(null);
          }}
          applicant={{ id: recipient.id, name: displayName(recipient) }}
          job={{ id: selectedJobForInterview.id, title: selectedJobForInterview.title }}
          applicationId={selectedJobForInterview?.applicationId || null}
          onSuccess={() => {
            setShowScheduleInterview(false);
            setSelectedJobForInterview(null);
            fetchThreadInterviews(recipient?.id);
          }}
        />
      )}

      {/* Profile Modal - reuses full ApplicantDetailModal */}
      <ApplicantDetailModal
        open={showProfileDialog}
        onOpenChange={setShowProfileDialog}
        applicant={recipient ? { seeker: recipient, id: null, cover: null, documents: [], custom_answer: null } : null}
        revealed={true}
      />

      <BottomNav profile={profile} />
    </div>
  );
};

export default Messages;